// ============================================
// Color Grading Post-Process Effect
// ============================================

import type { IRenderContext } from '../../core/IRenderContext';
import { 
  PostProcessEffect, 
  type PostProcessContext, 
  type PostProcessParameter 
} from '../PostProcessStack';
import { COLOR_GRADING_LAYOUT } from '../PostProcessLayouts';
import {
  type PipelineHandle,
  type ShaderHandle,
  NULL_HANDLE,
} from '../../types';
import { getCompiledShader } from '../ShaderCache';

/**
 * Color Grading post-processing effect
 * Adjusts exposure, contrast, saturation, temperature, and color
 */
export class ColorGradingEffect extends PostProcessEffect {
  readonly type = 'colorGrading';
  readonly displayName = 'Color Grading';
  
  // Parameters
  exposure = 0.0;
  contrast = 0.0;
  saturation = 1.0;
  temperature = 0.0;
  tint = 0.0;
  colorFilterR = 1.0;
  colorFilterG = 1.0;
  colorFilterB = 1.0;
  hueShift = 0.0;
  
  // GPU resources
  private shader: ShaderHandle = NULL_HANDLE;
  private pipelineHDR: PipelineHandle = NULL_HANDLE;
  private pipelineLDR: PipelineHandle = NULL_HANDLE;
  private uniformWriter = COLOR_GRADING_LAYOUT.createWriter();
  
  getParameters(): PostProcessParameter[] {
    return [
      { name: 'exposure', type: 'float', min: -5, max: 5, default: 0.0, description: 'Exposure adjustment' },
      { name: 'contrast', type: 'float', min: -1, max: 1, default: 0.0, description: 'Contrast' },
      { name: 'saturation', type: 'float', min: 0, max: 2, default: 1.0, description: 'Saturation' },
      { name: 'temperature', type: 'float', min: -100, max: 100, default: 0.0, description: 'Color temperature' },
      { name: 'tint', type: 'float', min: -100, max: 100, default: 0.0, description: 'Green/magenta tint' },
      { name: 'colorFilterR', type: 'float', min: 0, max: 2, default: 1.0, description: 'Color filter red' },
      { name: 'colorFilterG', type: 'float', min: 0, max: 2, default: 1.0, description: 'Color filter green' },
      { name: 'colorFilterB', type: 'float', min: 0, max: 2, default: 1.0, description: 'Color filter blue' },
      { name: 'hueShift', type: 'float', min: -0.5, max: 0.5, default: 0.0, description: 'Hue rotation' },
    ];
  }
  
  getParameterValue(name: string): number | boolean | number[] {
    switch (name) {
      case 'exposure': return this.exposure;
      case 'contrast': return this.contrast;
      case 'saturation': return this.saturation;
      case 'temperature': return this.temperature;
      case 'tint': return this.tint;
      case 'colorFilterR': return this.colorFilterR;
      case 'colorFilterG': return this.colorFilterG;
      case 'colorFilterB': return this.colorFilterB;
      case 'hueShift': return this.hueShift;
      default: return 0;
    }
  }
  
  setParameterValue(name: string, value: number | boolean | number[]): void {
    if (typeof value !== 'number') return;
    switch (name) {
      case 'exposure': this.exposure = value; break;
      case 'contrast': this.contrast = value; break;
      case 'saturation': this.saturation = value; break;
      case 'temperature': this.temperature = value; break;
      case 'tint': this.tint = value; break;
      case 'colorFilterR': this.colorFilterR = value; break;
      case 'colorFilterG': this.colorFilterG = value; break;
      case 'colorFilterB': this.colorFilterB = value; break;
      case 'hueShift': this.hueShift = value; break;
    }
  }
  
  protected onInitialize(ctx: IRenderContext): void {
    const hdrFormat = 'rgba16float' as const;
    const ldrFormat = ctx.getSwapChainFormat();
    
    const colorGradingWgsl = getCompiledShader('postprocess/color-grading');
    this.shader = ctx.createShader(colorGradingWgsl, colorGradingWgsl);
    
    this.pipelineHDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'color-grading-hdr',
    });
    this.pipelineLDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: ldrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'color-grading-ldr',
    });
  }
  
  protected onRender(context: PostProcessContext): void {
    const { ctx, source, width, height, intensity, isHdrTarget } = context;
    
    // NOTE: PostProcessStack already set render target and viewport

    this.uniformWriter.setAll({
      exposure: this.exposure * intensity,
      contrast: this.contrast * intensity,
      saturation: 1.0 + (this.saturation - 1.0) * intensity,
      temperature: this.temperature * intensity,
      tint: this.tint * intensity,
      hueShift: this.hueShift * intensity,
      _pad: [0, 0],
      colorFilter: [
        1.0 + (this.colorFilterR - 1.0) * intensity,
        1.0 + (this.colorFilterG - 1.0) * intensity,
        1.0 + (this.colorFilterB - 1.0) * intensity,
      ],
      _pad2: 0,
    });
    this.fullscreenPass(ctx, isHdrTarget ? this.pipelineHDR : this.pipelineLDR, this.uniformWriter.float32, [source]);
  }
  
  protected onDispose(ctx: IRenderContext): void {
    if (this.pipelineHDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineHDR);
    if (this.pipelineLDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineLDR);
    if (this.shader !== NULL_HANDLE) ctx.destroyShader(this.shader);
    this.pipelineHDR = NULL_HANDLE;
    this.pipelineLDR = NULL_HANDLE;
    this.shader = NULL_HANDLE;
  }
}
